import AbstractPiniaModel from './AbstractPiniaModel'
import { useAnswersStore } from '../pinia/stores/answers'
import {Question} from 'Models'
import ValidatorFactory from '@features/QuestionTypes/validatorFactory'
import {getPointsCalculatorFor} from '@features/QuestionTypes/pointsCalculatorFactory'
import {getMaxPointsCalculatorFor} from "@features/QuestionTypes/maxPointsCalculatorFactory"
import { Api } from '@/common/ajax/Api'
import { decMarkedQuestionsCount, incMarkedQuestionsCount, setMarkedQuestionsCountTo } from '@features/Bookmarks/model';
import { checkApiResponseForAuth, reportCaughtError } from '@features/_common/errorHandler';
import * as Sentry from '@sentry/browser'

/** Plain JSON from Pinia/session restore has no getSource(); models do. */
const questionSourceForStore = (question) => {
    if (question == null) return question
    if (typeof question.getSource === 'function') return question.getSource()
    if (typeof question.id === 'function') return Question.create(question).getSource()
    return question
}

const questionIdForDiagnostics = (question) => {
    if (question == null) return null
    if (typeof question.id === 'function') return question.id()
    return question.id ?? null
}

export class Answer extends AbstractPiniaModel {
    //#region Instance Code
        constructor(answer) { super(answer) }


        id()                        { return this.source.question.id; }
        question()                  { return Question.create(this.source.question); }
        questionIndex()             { return this.getByAliases(['questionIndex', 'question_index'], -1) }
        userInput()                 { return this.getByAliases(['userInput', 'benutzer_antworten'], []) }
        ignored()                   { return this.getByAliases(['ignored', 'ignoriert'], true) }
        mainCategoryId()            { return this.getByAliases(['mainCategoryID', 'main_category_id'], -1) }
        subCategoryId()             { return this.getByAliases(['subCategoryID', 'sub_category_id'], -1) }
        resultsSubCategoryId()      {
            return this.getByAliases(
                ['resultsSubCategoryID', 'results_sub_category_id'],
                this.subCategoryId()
            )
        }
        expectedDurationInSeconds() { return Number(this.getByAliases(['expectedDurationInSeconds', 'max_bearbeitungsdauer'], 0)) }
        actualDurationInSeconds()   { return Number(this.getByAliases(['actualDurationInSeconds', 'reale_bearbeitungsdauer'], 0)) }
        correctAnswers()            { return this.question().correctAnswers(); }
        countPerAnswer()            { return this.question().countPerAnswer() }
        lineEvaluation()            { return this.question().lineEvaluation() }
        audioUuid()                  { return this.source.audioUuid }

        realNumCorrectAnswers()
        {
            const actualQuestionCount = this.question().realQuestionsCount()

            if(!this.countPerAnswer() || actualQuestionCount===1)
                return this.isCorrect() ? 1 : 0

            return (actualQuestionCount * this.scoredPoints()) / this.maxPoints()
        }
        realNumWrongAnswers()
        {
            return this.question().realQuestionsCount() - this.realNumCorrectAnswers()
        }

        scoredPoints()
        {
            try {
                const calcScore = getPointsCalculatorFor(this.question())
                if (typeof calcScore !== 'function') return 0
                return calcScore(this)
            } catch (e) {
                reportCaughtError(e, 'answer.scored-points', { questionId: this.source?.question?.id })
                return 0
            }
        }
        maxPoints()
        {
            const calcMaxScore = getMaxPointsCalculatorFor(this.question())
            if (typeof calcMaxScore !== 'function')
                return 0
            return calcMaxScore(this)
        }

        isCorrect()     {
            try {
                const validator = ValidatorFactory.getFor(this.question())
                if (!validator || typeof validator.isAnswerCorrect !== 'function') return false
                return validator.isAnswerCorrect(this)
            } catch (e) {
                reportCaughtError(e, 'answer.is-correct', { questionId: this.source?.question?.id })
                return false
            }
        }
        hasUserInput()  { return this.userInput().length>0; }

        setToIgnored()  { this.dispatch('setToIgnored', this.id()); }

        questionName() { return this.question().answerOptions().name.substring(0, 20); }
    //#endregion Instance Code


    //#region Static Methods
        static piniaStore() { return useAnswersStore() }
        static create(answerSource) { return new Answer(answerSource); }

        static existsForId(id)  { return !this.findById(id).isEmpty(); }
        static isLastAnswer()   { return this.selectedUserAnswerIndex()>=this.userAnswersCount()-1 }

        static currentAnswer()  { return this.findByIndex(this.selectedUserAnswerIndex()) }
        static findIndexById(id){ return this.piniaStore().findIndexOfId(id); }
        static findById(id)     { return this.create(this.piniaStore().userAnswer(id));}
        static findByIndex(i)   { return this.create(this.piniaStore().userAnswers[i]); }
        static findForCurrentQuestion() { return this.create(this.findById(Question.currentQuestion().id())); }

        static userAnswers()        { return this.piniaStore().userAnswers.map(ua=>this.create(ua)) }
        static userAnswersCount()   { return this.piniaStore().userAnswers.length }
        static selectedUserAnswerIndex() { return this.piniaStore().selectedUserAnswerIndex }

        static setUserAnswersTo(answerSources)   {
            this.dispatch('resetAnswers')
            this.dispatch('setUserAnswersTo', answerSources)
        }
        static setToIgnoredById(id)             { this.dispatch('setToIgnored', id) }
        static sortByQuestionIndex()            { this.dispatch('sortUserAnswersByQuestionIndex') }
        static setSelectedUserAnswerIndex(idx)  { this.dispatch('setSelectedUserAnswerIndex', idx) }
        static incSelectedUserAnswerIndex()     { this.dispatch('incSelectedUserAnswerIndex') }
        static decSelectedUserAnswerIndex()     { this.dispatch('decSelectedUserAnswerIndex') }
        static incDurationOf(questionID, duration) {
                this.dispatch('incDurationOfQuestion', { questionID, duration })
        }
        static modifyUserAnswer(modificationDetails) {
            const raw = modificationDetails?.question
            const question = questionSourceForStore(raw)

            if (!question || question.id == null) {
                reportCaughtError(
                    new TypeError('modifyUserAnswer: question missing or without id'),
                    'answer.modify-user-answer',
                    {
                        key: modificationDetails?.key,
                        action: modificationDetails?.action,
                        hadGetSource: typeof raw?.getSource === 'function',
                        rawId: questionIdForDiagnostics(raw),
                    }
                )
                return
            }

            if (raw != null && typeof raw.getSource !== 'function') {
                try {
                    Sentry.addBreadcrumb({
                        category: 'answer',
                        level: 'info',
                        message: 'modifyUserAnswer: question was not a model instance',
                        data: {
                            questionId: question.id,
                            key: modificationDetails?.key,
                            action: modificationDetails?.action,
                        },
                    })
                } catch (_) { /* diagnostics must not throw */ }
            }

            modificationDetails.question = question
            this.dispatch('modifyUserAnswer', modificationDetails)
        }

        static markByID(id, data)     {
            this.dispatch('markQuestionInAnswer', id)
            incMarkedQuestionsCount()
            return Api.postMarkQuestionWith(true, id, data)
                      .then( ({data}) => setMarkedQuestionsCountTo(data) )
                      .catch( err => {
                          this.dispatch('unmarkQuestionInAnswer', id)
                          decMarkedQuestionsCount()
                          checkApiResponseForAuth(AbstractModel.ebus(), err)
                      })
        }
        static unmarkByID(id, data)   {
            this.dispatch('unmarkQuestionInAnswer', id)
            decMarkedQuestionsCount()
            return Api.postMarkQuestionWith(false, id, data)
                      .then( ({data}) => setMarkedQuestionsCountTo(data) )
                      .catch( err => {
                          this.dispatch('markQuestionInAnswer', id)
                          incMarkedQuestionsCount()
                          checkApiResponseForAuth(AbstractModel.ebus(), err)
                      })
        }

        // Returns the threshold in % above which the answer is considered answered correctly
        static getThresholdForSuccess() { return 50 /* percent */ }
    //#endregion Static Methods
}
